/** Custom attribute definitions per entity type (and per asset type scope). */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type AttributeDefinition,
  type AttributeDefinitionIn,
  type AttributeDefinitionPatch,
  type NumberSequence,
  useAllAttributeDefinitions,
  useAssetTypesAdmin,
  useDataTypes,
  useDeleteAttributeDefinition,
  useEntityTypes,
  useNumberSequences,
  useSaveAttributeDefinition,
  useTenantSettings,
  useUpdateNumberSequence,
  useUpdateTenantSettings,
} from "@/features/admin/reference/queries";
import { asText } from "@/shared/components/attribute-fields";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { useListState } from "@/shared/lib/list-state";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/ui/primitives";

interface DefValues extends Record<string, unknown> {
  entity_type: string;
  scope: string;
  key: string;
  label: string;
  label_ar: string;
  description: string;
  data_type: string;
  required: boolean;
  default: string;
  unit: string;
  group: string;
  sequence: string;
  min: string;
  max: string;
  pattern: string;
  is_filterable: boolean;
  is_active: boolean;
}

interface OptionRow {
  value: string;
  label: string;
}

const CHOICE_TYPES = new Set(["choice", "multi_choice"]);
const RANGE_TYPES = new Set(["integer", "number", "money"]);

/** Attribute definition create/edit dialog. */
export function AttributeDefinitionDialog({
  definition,
  entityType,
  onClose,
}: {
  definition?: AttributeDefinition;
  entityType: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveAttributeDefinition();
  const dataTypes = useDataTypes();
  const entityTypes = useEntityTypes();
  const assetTypes = useAssetTypesAdmin();
  const [options, setOptions] = useState<OptionRow[]>(
    ((definition?.options ?? []) as unknown as OptionRow[]).map((o) => ({
      value: o.value,
      label: o.label,
    })),
  );
  const validation = (definition?.validation ?? {}) as {
    min?: number;
    max?: number;
    pattern?: string;
  };
  const initial = useMemo<DefValues>(
    () => ({
      entity_type: definition?.entity_type ?? entityType,
      scope: definition?.scope ?? "",
      key: definition?.key ?? "",
      label: definition?.label ?? "",
      label_ar: definition?.label_i18n?.ar ?? "",
      description: definition?.description ?? "",
      data_type: definition?.data_type ?? "string",
      required: definition?.required ?? false,
      default: asText(definition?.default),
      unit: definition?.unit ?? "",
      group: definition?.group ?? "",
      sequence: String(definition?.sequence ?? 0),
      min: validation.min === undefined ? "" : String(validation.min),
      max: validation.max === undefined ? "" : String(validation.max),
      pattern: validation.pattern ?? "",
      is_filterable: definition?.is_filterable ?? false,
      is_active: definition?.is_active ?? true,
    }),
    [definition, entityType, validation.min, validation.max, validation.pattern],
  );
  const form = useForm<DefValues>({
    initial,
    schema: z.object({
      key: definition
        ? z.string()
        : z.string().regex(/^[a-z][a-z0-9_]{0,59}$/, t("attributes.keyFormat")),
      label: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "def",
  });
  const isChoice = CHOICE_TYPES.has(form.values.data_type);
  const isRange = RANGE_TYPES.has(form.values.data_type);
  const scoped =
    entityTypes.data?.find((e) => e.entity_type === form.values.entity_type)?.scoped ?? false;

  const parseDefault = (raw: string, type: string): unknown => {
    if (raw === "") {
      return null;
    }
    if (type === "integer" || type === "number" || type === "money") {
      return numberOrNull(raw);
    }
    if (type === "boolean") {
      return raw === "true";
    }
    return raw;
  };

  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const validationOut: Record<string, unknown> = {};
      if (isRange) {
        if (v.min !== "") {
          validationOut.min = Number(v.min);
        }
        if (v.max !== "") {
          validationOut.max = Number(v.max);
        }
      }
      if (v.data_type === "string" && v.pattern) {
        validationOut.pattern = v.pattern;
      }
      const common = {
        label: v.label.trim(),
        label_i18n: (v.label_ar ? { ar: v.label_ar } : {}) as Record<string, string>,
        description: v.description,
        required: v.required,
        default: parseDefault(v.default, v.data_type),
        options: isChoice
          ? options
              .filter((o) => o.value && o.label)
              .map((o) => ({ value: o.value, label: o.label, label_i18n: {} }))
          : [],
        unit: v.unit,
        validation: validationOut,
        group: v.group,
        sequence: Number(v.sequence) || 0,
        is_searchable: false,
        is_filterable: v.is_filterable,
      };
      const body: AttributeDefinitionIn | AttributeDefinitionPatch = definition
        ? { ...common, is_active: v.is_active }
        : {
            ...common,
            entity_type: v.entity_type,
            scope: scoped ? v.scope : "",
            key: v.key,
            data_type: v.data_type,
          };
      await save.mutateAsync({ id: definition?.id, body });
    });
    if (ok) {
      onClose();
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={definition ? t("attributes.edit") : t("attributes.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      {definition?.is_system ? (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
          {t("attributes.systemNote")}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="def-entity_type" label={t("attributes.entity")}>
          <Select
            id="def-entity_type"
            value={form.values.entity_type}
            onChange={(e) => form.patch({ entity_type: e.target.value, scope: "" })}
            disabled={!!definition}
          >
            {(entityTypes.data ?? []).map((e) => (
              <option key={e.entity_type} value={e.entity_type}>
                {t(`attributes.entities.${e.entity_type}`)}
              </option>
            ))}
          </Select>
        </Field>
        {scoped ? (
          <Field id="def-scope" label={t("attributes.scope")} hint={t("attributes.scopeHint")}>
            <Select
              id="def-scope"
              value={form.values.scope}
              onChange={(e) => form.set("scope", e.target.value)}
              disabled={!!definition}
            >
              <option value="">{t("attributes.allTypes")}</option>
              {(assetTypes.data ?? []).map((at) => (
                <option key={at.key} value={at.key}>
                  {at.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field
          id="def-key"
          label={t("app.key")}
          error={form.errors.key}
          hint={t("attributes.keyHint")}
        >
          <Input {...form.field("key")} disabled={!!definition} />
        </Field>
        <Field id="def-data_type" label={t("attributes.dataType")}>
          <Select
            id="def-data_type"
            value={form.values.data_type}
            onChange={(e) => form.set("data_type", e.target.value)}
            disabled={!!definition}
          >
            {(dataTypes.data ?? []).map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="def-label" label={t("attributes.label")} error={form.errors.label}>
          <Input {...form.field("label")} />
        </Field>
        <Field id="def-label_ar" label={t("attributes.labelAr")}>
          <Input dir="rtl" {...form.field("label_ar")} />
        </Field>
        <Field id="def-group" label={t("attributes.group")} hint={t("attributes.groupHint")}>
          <Input {...form.field("group")} />
        </Field>
        <Field id="def-sequence" label={t("attributes.sequence")}>
          <Input type="number" min="0" {...form.field("sequence")} />
        </Field>
        <Field id="def-unit" label={t("attributes.unit")}>
          <Input {...form.field("unit")} />
        </Field>
        <Field id="def-default" label={t("attributes.default")}>
          <Input {...form.field("default")} />
        </Field>
        {isRange ? (
          <>
            <Field id="def-min" label={t("attributes.min")}>
              <Input type="number" step="any" {...form.field("min")} />
            </Field>
            <Field id="def-max" label={t("attributes.max")}>
              <Input type="number" step="any" {...form.field("max")} />
            </Field>
          </>
        ) : null}
        {form.values.data_type === "string" ? (
          <Field
            id="def-pattern"
            label={t("attributes.pattern")}
            hint={t("attributes.patternHint")}
          >
            <Input {...form.field("pattern")} />
          </Field>
        ) : null}
      </div>
      <Field id="def-description" label={t("app.description")}>
        <Textarea {...form.field("description")} />
      </Field>
      {isChoice ? (
        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{t("attributes.options")}</h3>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setOptions([...options, { value: "", label: "" }])}
            >
              {t("attributes.addOption")}
            </Button>
          </div>
          {options.map((o, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            >
              <Input
                aria-label={`${t("attributes.optionValue")} ${i + 1}`}
                placeholder={t("attributes.optionValue")}
                value={o.value}
                onChange={(e) =>
                  setOptions(
                    options.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)),
                  )
                }
              />
              <Input
                aria-label={`${t("attributes.optionLabel")} ${i + 1}`}
                placeholder={t("attributes.optionLabel")}
                value={o.label}
                onChange={(e) =>
                  setOptions(
                    options.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)),
                  )
                }
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`${t("app.delete")} ${i + 1}`}
                onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-4">
        <Checkbox
          id="def-required"
          label={t("app.required")}
          checked={form.values.required}
          onChange={(e) => form.set("required", e.target.checked)}
        />
        <Checkbox
          id="def-is_filterable"
          label={t("attributes.filterable")}
          checked={form.values.is_filterable}
          onChange={(e) => form.set("is_filterable", e.target.checked)}
        />
        {definition ? (
          <Checkbox
            id="def-is_active"
            label={t("app.active")}
            checked={form.values.is_active}
            onChange={(e) => form.set("is_active", e.target.checked)}
          />
        ) : null}
      </div>
    </FormDialog>
  );
}

/** Edit dialog for one per-tenant NumberSequence. */
function NumberSequenceDialog({
  sequence,
  onClose,
}: {
  sequence: NumberSequence;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateNumberSequence();
  const form = useForm({
    initial: {
      pattern: sequence.pattern,
      next_value: String(sequence.next_value),
      reset_yearly: sequence.reset_yearly,
    },
    schema: z.object({
      pattern: z.string().trim().min(3, t("app.required")),
      next_value: z.string().min(1, t("app.required")),
    }),
    idPrefix: "seq",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      update.mutateAsync({
        key: sequence.key,
        body: {
          pattern: v.pattern.trim(),
          next_value: Number(v.next_value),
          reset_yearly: v.reset_yearly,
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${t("attributes.editSequence")} · ${sequence.key}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field
        id="seq-pattern"
        label={t("attributes.pattern")}
        hint={t("attributes.patternHint")}
        error={form.errors.pattern}
      >
        <Input {...form.field("pattern")} />
      </Field>
      <Field id="seq-next_value" label={t("attributes.nextValue")} error={form.errors.next_value}>
        <Input type="number" min={sequence.next_value} {...form.field("next_value")} />
      </Field>
      <Checkbox
        id="seq-reset_yearly"
        label={t("attributes.resetYearly")}
        checked={form.values.reset_yearly}
        onChange={(e) => form.set("reset_yearly", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Per-tenant general settings form. */
function TenantGeneralPanel(): ReactElement {
  const settings = useTenantSettings();
  const update = useUpdateTenantSettings();
  const [saved, setSaved] = useState(false);
  if (settings.isPending) {
    return <LoadingState />;
  }
  if (settings.isError) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  return (
    <TenantGeneralForm
      data={settings.data}
      saved={saved}
      onSave={async (patch) => {
        setSaved(false);
        await update.mutateAsync(patch);
        setSaved(true);
      }}
    />
  );
}

function TenantGeneralForm({
  data,
  saved,
  onSave,
}: {
  data: {
    name: string;
    slug: string;
    base_currency: string;
    timezone: string;
    default_locale: string;
    require_wo_verification: boolean;
  };
  saved: boolean;
  onSave: (patch: {
    name: string;
    timezone: string;
    default_locale: string;
    require_wo_verification: boolean;
  }) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const initial = useMemo(
    () => ({
      name: data.name,
      timezone: data.timezone,
      default_locale: data.default_locale,
      require_wo_verification: data.require_wo_verification,
    }),
    [data],
  );
  const form = useForm({
    initial,
    schema: z.object({
      name: z.string().trim().min(1, t("app.required")),
      timezone: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "tenant-settings",
  });
  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    await form.submit((v) =>
      onSave({
        name: v.name.trim(),
        timezone: v.timezone.trim(),
        default_locale: v.default_locale,
        require_wo_verification: v.require_wo_verification,
      }),
    );
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("attributes.generalSettings")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void submit(e)} className="max-w-xl space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="tenant-settings-name" label={t("app.name")} error={form.errors.name}>
              <Input {...form.field("name")} />
            </Field>
            <Field id="tenant-settings-currency" label={t("attributes.baseCurrency")}>
              <Input id="tenant-settings-currency" value={data.base_currency} disabled />
            </Field>
            <Field
              id="tenant-settings-timezone"
              label={t("sites.timezone")}
              error={form.errors.timezone}
            >
              <Input {...form.field("timezone")} />
            </Field>
            <Field id="tenant-settings-locale" label={t("attributes.defaultLocale")}>
              <Select
                id="tenant-settings-locale"
                value={form.values.default_locale}
                onChange={(e) => form.set("default_locale", e.target.value)}
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="ar">العربية</option>
              </Select>
            </Field>
          </div>
          <Checkbox
            id="tenant-settings-verify"
            label={t("attributes.requireWoVerification")}
            checked={form.values.require_wo_verification}
            onChange={(e) => form.set("require_wo_verification", e.target.checked)}
          />
          {form.errors._ ? (
            <p role="alert" className="text-sm text-destructive">
              {form.errors._}
            </p>
          ) : null}
          {saved ? (
            <p role="status" className="text-sm text-emerald-700">
              {t("attributes.saved")}
            </p>
          ) : null}
          <Button type="submit" disabled={form.submitting}>
            {t("app.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Attribute definitions, tenant general settings & numbering sequences page. */
export function AttributesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = useListState(".");
  const activeTab =
    list.search.tab === "numbering"
      ? "numbering"
      : list.search.tab === "general"
        ? "general"
        : "attributes";
  const entityType = list.search.entity_type ?? "asset";
  const setEntityType = (value: string): void =>
    list.update({ entity_type: value, q: undefined, sort: undefined });
  const entityTypes = useEntityTypes();
  const defs = useAllAttributeDefinitions(entityType);
  const assetTypes = useAssetTypesAdmin();
  const remove = useDeleteAttributeDefinition();
  const manage = can("platform.tenant.settings");
  const sequences = useNumberSequences(manage && activeTab === "numbering");
  const [dialog, setDialog] = useState<{ definition?: AttributeDefinition } | null>(null);
  const [seqDialog, setSeqDialog] = useState<NumberSequence | null>(null);
  const scopeName = (scope: string): string =>
    scope
      ? (assetTypes.data?.find((x) => x.key === scope)?.name ?? scope)
      : t("attributes.allTypes");

  const columns: ColumnDef<AttributeDefinition>[] = [
    {
      id: "label",
      header: t("attributes.label"),
      cell: (c) => (
        <div>
          <div className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
            {c.row.original.label}{" "}
            {c.row.original.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
          </div>
          <div className="font-mono text-xs text-muted-foreground">{c.row.original.key}</div>
        </div>
      ),
    },
    ...(entityType === "asset"
      ? [
          {
            id: "scope",
            header: t("attributes.scope"),
            cell: (c): string => scopeName(c.row.original.scope),
          } satisfies ColumnDef<AttributeDefinition>,
        ]
      : []),
    {
      id: "type",
      header: t("attributes.dataType"),
      cell: (c) => <Badge>{c.row.original.data_type}</Badge>,
    },
    { id: "group", header: t("attributes.group"), cell: (c) => c.row.original.group || "—" },
    {
      id: "required",
      header: t("app.required"),
      cell: (c) => (c.row.original.required ? t("app.yes") : t("app.no")),
    },
    { id: "unit", header: t("attributes.unit"), cell: (c) => c.row.original.unit || "—" },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ definition: c.row.original })}
                >
                  {t("app.edit")}
                </Button>
                {!c.row.original.is_system ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => {
                      if (globalThis.confirm(t("app.confirmDelete"))) {
                        remove.mutate(c.row.original.id);
                      }
                    }}
                  >
                    {t("app.delete")}
                  </Button>
                ) : null}
              </div>
            ),
          } satisfies ColumnDef<AttributeDefinition>,
        ]
      : []),
  ];

  const seqColumns: ColumnDef<NumberSequence>[] = [
    {
      id: "key",
      header: t("app.key"),
      cell: (c) => <span className="font-mono text-xs font-medium">{c.row.original.key}</span>,
    },
    {
      id: "pattern",
      header: t("attributes.pattern"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.pattern}</span>,
    },
    {
      id: "preview",
      header: t("attributes.preview"),
      cell: (c) => <Badge tone="outline">{c.row.original.preview}</Badge>,
    },
    {
      id: "next_value",
      header: t("attributes.nextValue"),
      cell: (c) => <span className="tabular-nums">{c.row.original.next_value}</span>,
    },
    {
      id: "reset_yearly",
      header: t("attributes.resetYearly"),
      cell: (c) => (c.row.original.reset_yearly ? t("app.yes") : t("app.no")),
    },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button size="sm" variant="ghost" onClick={() => setSeqDialog(c.row.original)}>
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<NumberSequence>,
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title={t("attributes.title")}
        subtitle={t("attributes.subtitle")}
        actions={
          manage && activeTab === "attributes" ? (
            <Button onClick={() => setDialog({})}>{t("attributes.new")}</Button>
          ) : null
        }
      />
      {dialog ? (
        <AttributeDefinitionDialog
          definition={dialog.definition}
          entityType={entityType}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {seqDialog ? (
        <NumberSequenceDialog sequence={seqDialog} onClose={() => setSeqDialog(null)} />
      ) : null}
      <Tabs
        value={activeTab}
        onValueChange={(tab) => list.update({ tab, q: undefined, sort: undefined })}
      >
        <TabsList className="mb-3">
          <TabsTrigger value="attributes">{t("attributes.title")}</TabsTrigger>
          {manage ? (
            <>
              <TabsTrigger value="numbering">{t("attributes.numbering")}</TabsTrigger>
              <TabsTrigger value="general">{t("attributes.generalSettings")}</TabsTrigger>
            </>
          ) : null}
        </TabsList>
        <TabsContent value="attributes">
          <div className="mb-3">
            <Select
              aria-label={t("attributes.entity")}
              className="w-56"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            >
              {(entityTypes.data ?? [{ entity_type: "asset", scoped: true }]).map((e) => (
                <option key={e.entity_type} value={e.entity_type}>
                  {t(`attributes.entities.${e.entity_type}`)}
                </option>
              ))}
            </Select>
          </div>
          {remove.isError ? (
            <p role="alert" className="mb-2 text-sm text-destructive">
              {remove.error.message}
            </p>
          ) : null}
          {defs.isPending ? (
            <LoadingState />
          ) : defs.isError ? (
            <ErrorState error={defs.error} onRetry={() => void defs.refetch()} />
          ) : (
            <CompleteDataTable
              columns={columns}
              data={defs.data}
              getSearchText={(definition) => `${definition.key} ${definition.label}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.key")}, ${t("attributes.label")}`,
              })}
              sortFields={[
                {
                  value: "label",
                  label: t("attributes.label"),
                  valueOf: (definition) => definition.label,
                },
                {
                  value: "group",
                  label: t("attributes.group"),
                  valueOf: (definition) => definition.group,
                },
              ]}
              minWidth={800}
              getRowId={(d) => d.id}
              total={defs.data.length}
            />
          )}
        </TabsContent>
        <TabsContent value="numbering">
          {sequences.isPending ? (
            <LoadingState />
          ) : sequences.isError ? (
            <ErrorState error={sequences.error} onRetry={() => void sequences.refetch()} />
          ) : (
            <CompleteDataTable
              columns={seqColumns}
              data={sequences.data ?? []}
              getSearchText={(s) => `${s.key} ${s.pattern} ${s.preview}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.key")}, ${t("attributes.pattern")}`,
              })}
              sortFields={[
                { value: "key", label: t("app.key"), valueOf: (s) => s.key },
                {
                  value: "next_value",
                  label: t("attributes.nextValue"),
                  valueOf: (s) => s.next_value,
                },
              ]}
              minWidth={650}
              getRowId={(s) => s.key}
              total={(sequences.data ?? []).length}
            />
          )}
        </TabsContent>
        <TabsContent value="general">
          <TenantGeneralPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
